import React, { useState, useCallback, Suspense } from 'react';
import { MainLayout } from './components/layout/MainLayout';
import { GameContainer } from './components/game/GameContainer';
import { ModalsContainer } from './components/modals/ModalsContainer';
import { AdminPanel } from './components/admin/AdminPanel';
import { WalletPanel } from './components/WalletPanel';
import { useGameState } from './hooks/useGameState';
import { useGameCalculations } from './hooks/useGameCalculations';
import { ErrorCode, createError, AppError } from './types/errors';
import toast from 'react-hot-toast';
import { ErrorBoundary } from './components/ErrorBoundary';

// Get admin wallet from env
const ADMIN_WALLET = import.meta.env.VITE_ADMIN_WALLET?.toLowerCase() || '';

function App() {
  const [walletAddress, setWalletAddress] = useState<string | null>('0x9168fa2d41760a451F37be85D89650777faE262d'); // Demo wallet
  const { gameState, updateGameState, error: gameError } = useGameState(walletAddress);
  const { calculateClickGains } = useGameCalculations(walletAddress);

  const [isShopOpen, setIsShopOpen] = useState(false);
  const [isStatsOpen, setIsStatsOpen] = useState(false);
  const [isAchievementsOpen, setIsAchievementsOpen] = useState(false);
  const [isFortuneWheelOpen, setIsFortuneWheelOpen] = useState(false);
  const [isAdminOpen, setIsAdminOpen] = useState(false);
  const [isWalletOpen, setIsWalletOpen] = useState(false);
  const [appError, setAppError] = useState<AppError | null>(null);

  const [wheelSegments] = useState([]);
  const [spinCost] = useState(50);

  // Check if current wallet is admin
  const isAdmin = walletAddress?.toLowerCase() === ADMIN_WALLET;

  const handleMiningClick = useCallback(() => {
    try {
      const { value: coinsEarned, isCritical } = calculateClickGains();
      
      updateGameState({
        clicks: gameState.clicks + 1,
        miningClicks: gameState.miningClicks + 1,
        balance: gameState.balance + coinsEarned,
        pendingClicks: gameState.pendingClicks + 1,
        lastCritical: isCritical,
        lastEarned: coinsEarned
      });
    } catch (error) {
      const appError = createError(
        ErrorCode.APP_STATE_CORRUPTED,
        'Failed to process mining click',
        { originalError: error }
      );
      setAppError(appError);
      console.error(appError);
      toast.error('Failed to process click');
    }
  }, [gameState, calculateClickGains, updateGameState]);

  const handleToggleAutoClicker = useCallback(() => {
    updateGameState({
      autoClickerActive: !gameState.autoClickerActive
    });
  }, [gameState.autoClickerActive, updateGameState]);

  const handlePurchase = useCallback((newBalance: number) => {
    updateGameState({
      balance: newBalance
    });
  }, [updateGameState]);

  if (appError || gameError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white p-4">
        <div className="bg-red-900/50 rounded-lg p-6 max-w-lg w-full">
          <h1 className="text-2xl font-bold mb-4">Error</h1>
          <p className="text-red-300 mb-4">{appError?.message || gameError?.message}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-red-500 hover:bg-red-600 px-4 py-2 rounded"
          >
            Reload Game
          </button>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <MainLayout
        onOpenStats={() => setIsStatsOpen(true)}
        onOpenShop={() => setIsShopOpen(true)}
        onOpenAchievements={() => setIsAchievementsOpen(true)}
        onOpenFortuneWheel={() => setIsFortuneWheelOpen(true)}
        onOpenAdmin={() => setIsAdminOpen(true)}
        onOpenWallet={() => setIsWalletOpen(true)}
        walletAddress={walletAddress}
        isAdmin={isAdmin}
      >
        <GameContainer
          gameState={gameState}
          onMiningClick={handleMiningClick}
          account={walletAddress}
        />

        <ModalsContainer
          isShopOpen={isShopOpen}
          isStatsOpen={isStatsOpen}
          isAchievementsOpen={isAchievementsOpen}
          isFortuneWheelOpen={isFortuneWheelOpen}
          gameState={gameState}
          wheelSegments={wheelSegments}
          spinCost={spinCost}
          autoClickerActive={gameState.autoClickerActive}
          onCloseShop={() => setIsShopOpen(false)}
          onCloseStats={() => setIsStatsOpen(false)}
          onCloseAchievements={() => setIsAchievementsOpen(false)}
          onCloseFortuneWheel={() => setIsFortuneWheelOpen(false)}
          onToggleAutoClicker={handleToggleAutoClicker}
          onPurchase={handlePurchase}
          walletAddress={walletAddress}
        />

        {isAdmin && (
          <AdminPanel
            isOpen={isAdminOpen}
            onClose={() => setIsAdminOpen(false)}
            currentWallet={walletAddress || ''}
            contractAddress={import.meta.env.VITE_CONTRACT_ADDRESS}
            mainWallet={ADMIN_WALLET}
          />
        )}

        <WalletPanel
          isOpen={isWalletOpen}
          onClose={() => setIsWalletOpen(false)}
          walletAddress={walletAddress}
        />
      </MainLayout>
    </ErrorBoundary>
  );
}

export default App;